import { useEffect, useState } from 'react';
import { motion, useAnimate } from 'motion/react';
import clsx from 'clsx';
import type { Card as CardType } from '@/types';
import BorderImage from '@/assets/images/2-circles-dashes.svg';
import Logo from '@/assets/images/2-target-your-mate.png';
import {
  CORRECT_PAIR_INDEX,
  FLIP_BACK_DELAY,
  HAND_ANIMATION_DELAY_BETWEEN_MILLISECONDS,
  HAND_ANIMATION_FADING_MILLISECONDS,
  HAND_CSS_SELECTOR_PREFIX,
  HANDS_FOR_ANIMATION,
} from './config';
import { Card } from './Card';
import { createInitialCards } from './utils';
import { useBackgroundAnimation } from '@/hooks';

interface Props {
  onComplete: () => void;
  onExitStart: () => void;
}

export const Step2 = ({ onComplete, onExitStart }: Props) => {
  const [cards, setCards] = useState<CardType[]>(createInitialCards);
  const [flippedCardIds, setFlippedCardIds] = useState<number[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [allMatched, setAllMatched] = useState(false);
  const [selectedPairId, setSelectedPairId] = useState<number | null>(null);
  const [isShaking, setIsShaking] = useState(false);
  const [validationLayout, setValidationLayout] = useState(false);
  const backgroundAnimationScope = useBackgroundAnimation(
    HANDS_FOR_ANIMATION,
    HAND_CSS_SELECTOR_PREFIX,
    HAND_ANIMATION_FADING_MILLISECONDS,
    HAND_ANIMATION_DELAY_BETWEEN_MILLISECONDS,
  );
  const [pageTransitionScope, animate] = useAnimate();

  useEffect(() => {
    const enterAnimation = async () => {
      // initial state, instant
      await animate(pageTransitionScope.current, { opacity: 0, background: 'var(--color-green)' }, { duration: 0 });

      // actual animation
      await animate(pageTransitionScope.current, { opacity: 1 }, { duration: 1, ease: 'easeInOut' });
    };

    void enterAnimation();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exitAnimation = async () => {
    void animate('&>*', { opacity: 0, scale: 0.9 }, { duration: 0.5 });

    return animate(pageTransitionScope.current, { background: 'var(--color-red)' }, { duration: 2 });
  };

  // Get unique cards (one from each pair) for validation mode
  const uniqueCards = cards.filter((card, index, self) => self.findIndex(c => c.pairId === card.pairId) === index);

  const handleFlipCardClick = (clickedCard: CardType) => {
    // Ignore click:
    //   - If checking or card already matched/flipped
    //   - If already 2 cards flipped
    if (isChecking || clickedCard.isMatched || clickedCard.isFlipped || flippedCardIds.length >= 2) return;

    // Flip the card
    const newCards = cards.map(card => (card.id === clickedCard.id ? { ...card, isFlipped: true } : card));
    setCards(newCards);

    const newFlippedCards = [...flippedCardIds, clickedCard.id];
    setFlippedCardIds(newFlippedCards);

    if (newFlippedCards.length === 2) {
      setIsChecking(true);
      const [firstId, secondId] = newFlippedCards;
      const firstCard = cards.find(c => c.id === firstId)!;
      const secondCard = cards.find(c => c.id === secondId)!;

      if (firstCard.pairId === secondCard.pairId) {
        // Match found!
        const matchedCards = newCards.map(card => (card.id === firstId || card.id === secondId ? { ...card, isMatched: true } : card));

        if (matchedCards.every(card => card.isMatched)) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setAllMatched(true);
          // Trigger transition: fade out all cards, then show validation layout
          setTimeout(() => {
            setValidationLayout(true);
          }, 1000); // Wait for fade out
        }

        setCards(matchedCards);
        setFlippedCardIds([]);
        setIsChecking(false);
      } else {
        // No match, flip back after delay
        setTimeout(() => {
          const flippedBackCards = newCards.map(card => (card.id === firstId || card.id === secondId ? { ...card, isFlipped: false } : card));
          setCards(flippedBackCards);
          setFlippedCardIds([]);
          setIsChecking(false);
        }, FLIP_BACK_DELAY);
      }
    }
  };

  const handleValidationClick = (clickedCard: CardType) => {
    if (selectedPairId === clickedCard.pairId) {
      // Deselect if clicking the same pair
      setSelectedPairId(null);
    } else {
      setSelectedPairId(clickedCard.pairId);
    }
  };

  const handleValidate = () => {
    if (selectedPairId === CORRECT_PAIR_INDEX) {
      // Correct pair selected!
      handleStepSuccess();
    } else {
      // Wrong pair, shake animation
      setIsShaking(true);

      setSelectedPairId(null);
      setTimeout(() => setIsShaking(false), 500);
    }
  };

  const handleStepSuccess = () => {
    onExitStart();
    void exitAnimation().then(onComplete);
  };

  return (
    <div className="min-h-dvh w-screen bg-green flex flex-col">
      <div className="flex w-full">
        <div className="flex-1 p-3 bg-beige flex items-center justify-center text-center text-dark-beige/50">Find the&nbsp;Password</div>
        <div className="flex-1 p-3 bg-green flex items-center justify-center text-center text-light-green font-bold">Target your&nbsp;Mate</div>
        <div className="flex-1 p-3 bg-red flex items-center justify-center text-center text-dark-red/50">*********</div>
      </div>

      <div
        ref={pageTransitionScope}
        className="relative flex-1 flex items-center justify-center gap-12 py-8 px-4 flex-col border-image-dashed text-light-green"
        style={{
          borderImageSource: `url(${BorderImage})`,
        }}
      >
        <div ref={backgroundAnimationScope} className="absolute size-full pointer-events-none">
          {HANDS_FOR_ANIMATION.map((SVGElement, index) => (
            <SVGElement
              key={index}
              className={`${HAND_CSS_SELECTOR_PREFIX}-${index} absolute pointer-events-none *:max-w-full *:max-h-full text-current/25 size-16 md:size-28`}
              style={{ opacity: 0 }}
            />
          ))}
        </div>

        <div className="flex xl:hidden flex-col gap-8 items-center">
          <img src={Logo} alt="TARGET your MATE" className="h-24 w-auto" />

          {validationLayout && (
            <div className="flex flex-col gap-8 items-center">
              <span className="text-center">Retrouvez le membre de l’agence avec l’indice en votre possession</span>

              <button onClick={handleValidate} className="button bg-green">
                Valider mon choix
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-6 xl:gap-16 w-full max-w-7xl">
          {validationLayout ? (
            <motion.div
              key="2-cards-left"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: 'easeInOut' }}
              className={clsx(isShaking && 'animate-shake', 'grid grid-cols-1 xl:grid-cols-2 gap-y-6 xl:gap-y-16 gap-x-8 flex-1')}
            >
              {uniqueCards.slice(0, 2).map(card => (
                <Card key={card.id} card={card} onClick={() => handleValidationClick(card)} isSelected={selectedPairId === card.pairId} skewToLeft />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="4-cards-left"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={allMatched ? { opacity: 0, scale: 0.9 } : { opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: 'easeInOut', delay: 0.25 }}
              className="grid grid-cols-1 xl:grid-cols-2 gap-y-6 xl:gap-y-16 gap-x-8 flex-1"
            >
              {cards.slice(0, 4).map(card => (
                <Card key={card.id} card={card} onClick={() => handleFlipCardClick(card)} skewToLeft />
              ))}
            </motion.div>
          )}

          <div className="hidden xl:block">
            <img src={Logo} alt="TARGET your MATE" className="h-40 w-auto" />
          </div>

          {validationLayout ? (
            <motion.div
              key="2-cards-right"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: 'easeInOut' }}
              className={clsx(isShaking && 'animate-shake', 'grid grid-cols-1 xl:grid-cols-2 gap-y-6 xl:gap-y-16 gap-x-8 flex-1')}
            >
              {uniqueCards.slice(2, 4).map(card => (
                <Card key={card.id} card={card} onClick={() => handleValidationClick(card)} isSelected={selectedPairId === card.pairId} />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="4-cards-right"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={allMatched ? { opacity: 0, scale: 0.9 } : { opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: 'easeInOut', delay: 0.25 }}
              className="grid grid-cols-1 xl:grid-cols-2 gap-y-6 xl:gap-y-16 gap-x-8 flex-1"
            >
              {cards.slice(4, 8).map(card => (
                <Card key={card.id} card={card} onClick={() => handleFlipCardClick(card)} />
              ))}
            </motion.div>
          )}
        </div>

        {validationLayout && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, ease: 'easeInOut' }}
            className="hidden xl:flex fixed bottom-20 flex-col gap-8 items-center"
          >
            <span className="text-center">Retrouvez le membre de l’agence avec l’indice en votre possession</span>

            <button onClick={handleValidate} className="button bg-green">
              Valider mon choix
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};
